"use client";

import React, { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/packages/context/CartContext";
import { toast } from "sonner";
import { fetchCouponByCode } from "@/api-service/discount";
import { useAuth } from "@/packages/context/AuthContext";
import Price from "../common/Price";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { Input } from "../ui/input";
import LABELS from "@/components/global-constant/src/general";
import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";
import { Lock, CheckCircle2, ArrowRight } from "lucide-react";

export default function CartSummary() {
  const { cartItems, couponResponse, dispatch } = useCart();
  const { user, isLoggedIn } = useAuth();
  const { fastPush } = useFastRouter();
  const [couponCode, setCouponCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const summaryRef = useRef<HTMLDivElement>(null);
  const [isSummaryInView, setIsSummaryInView] = useState(false);

  const couponApplied = Boolean(couponResponse);

  // IntersectionObserver to detect when the main Order Summary is in the mobile viewport
  useEffect(() => {
    const target = summaryRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSummaryInView(entry.isIntersecting);
      },
      {
        threshold: 0.1, // Trigger when 10% of Order Summary enters/leaves viewport
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const handleScrollToSummary = () => {
    if (summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  let subtotal = 0;
  let tax = 0;

  cartItems.forEach((item) => {
    const taxRate = item.producttaxes?.[0]?.value || 0; // e.g. 18 means 18%
    const totalWithTax = (item.finalPrice || item.price) * item.quantity;
    const basePrice = totalWithTax / (1 + taxRate / 100);
    const itemTax = totalWithTax - basePrice;

    subtotal += basePrice;
    tax += itemTax;
  });

  // Discount calculation
  const discount = useMemo(() => {
    if (!couponApplied || !couponResponse) return 0;

    const coupon = couponResponse as Record<string, unknown>;
    const discountType = coupon.discountType as number;
    const value = coupon.value as number;
    const minSpend = coupon.minSpend as number | undefined;
    const maxDiscountAmount = coupon.maxDiscountAmount as number | undefined;
    const userBased = coupon.userBased as boolean;
    const userId = coupon.userId as string[] | string | undefined;
    const discountBased = coupon.discountBased as number | undefined;
    const validProducts: number[] = Array.isArray(coupon.validProducts)
      ? (coupon.validProducts as number[])
      : [];

    const eligibleSubtotal = cartItems.reduce((acc, item) => {
      if (typeof item.id === "string" && item.id.includes("-free")) {
        return acc;
      }
      const taxRate = item.producttaxes?.[0]?.value || 0;
      const totalWithTax = (item.finalPrice || item.price) * item.quantity;
      const basePrice = totalWithTax / (1 + taxRate / 100);

      if (discountBased && discountBased !== 0) {
        if (validProducts.includes(Number(item.id))) {
          return acc + basePrice;
        }
        return acc;
      }
      return acc + basePrice;
    }, 0);

    if (minSpend && eligibleSubtotal < minSpend) return 0;

    if (userBased && user) {
      let eligible = false;
      if (Array.isArray(userId)) {
        eligible = userId.includes(String(user.id));
      } else if (typeof userId === "string") {
        eligible = userId === String(user.id);
      }
      if (!eligible) return 0;
    }

    let calculatedDiscount = 0;
    if (discountType === 0) {
      calculatedDiscount = (eligibleSubtotal * value) / 100;
    } else if (discountType === 1) {
      calculatedDiscount = value;
    }

    if (maxDiscountAmount) {
      calculatedDiscount = Math.min(calculatedDiscount, maxDiscountAmount);
    }

    return Math.min(calculatedDiscount, eligibleSubtotal);
  }, [couponApplied, couponResponse, cartItems, user]);

  const total = subtotal - discount + tax;

  // Apply coupon handler
  const handleApplyCoupon = async () => {
    setError("");
    if (!couponCode.trim()) {
      setError(LABELS.CART_SUMMARY.COUPON_ENTER_ERROR);
      return;
    }
    try {
      setLoading(true);
      const result = await fetchCouponByCode(couponCode);
      const discountData = result?.data?.discount;
      const validProducts = result?.data?.validProducts || [];

      if (discountData?.userBased === true) {
        if (!user) {
          toast.error("Please login to apply this coupon.");
          return;
        }
        const eligibleUser = discountData?.userId?.includes(user.id);
        if (!eligibleUser) {
          toast.error("This user cannot avail this coupon.");
          return;
        }
      }

      if (discountData?.discountBased != 0) {
        const eligibleProducts = cartItems.filter((product) => {
          if (typeof product.id === "string" && product.id.includes("-free")) {
            return false;
          }
          return validProducts.includes(Number(product.id));
        });

        if (eligibleProducts.length <= 0) {
          toast.error(
            "The selected products are not eligible for this coupon."
          );
          setCouponCode("");
          return;
        }
      }

      dispatch({
        type: "SET_COUPON",
        payload: { ...discountData, validProducts },
      });
      toast.success(LABELS.CART_SUMMARY.COUPON_SUCCESS);
    } catch (err: unknown) {
      console.error("Error applying coupon:", err);
      setCouponCode("");
      const errRecord = (err as Record<string, unknown>)?.response as
        | Record<string, unknown>
        | undefined;
      const errData = errRecord?.data as Record<string, unknown> | undefined;
      toast.error((errData?.message as string) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    dispatch({ type: "CLEAR_COUPON" });
    setCouponCode("");
    setError("");
  };

  // Render Inner Content (Coupon, Breakdown, Trust Badges)
  const renderSummaryDetails = () => (
    <div className="space-y-5">
      {/* Coupon Code Section */}
      {isLoggedIn && (
        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-700">
            {LABELS.CART_SUMMARY.COUPON_LABEL}
          </label>
          {couponApplied ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-3 py-2 text-xs flex items-center justify-between font-medium">
              <span className="font-bold tracking-wider">
                {String((couponResponse as Record<string, unknown>).code ?? "")}
              </span>
              <button
                type="button"
                onClick={handleRemoveCoupon}
                className="text-rose-500 hover:text-rose-700 text-lg font-bold px-2 cursor-pointer transition-colors"
                title="Remove coupon"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                placeholder={LABELS.CART_SUMMARY.COUPON_PLACEHOLDER}
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                className="h-11 border border-slate-200 rounded-xl px-3.5 text-xs text-slate-800 focus:border-[var(--brand-accent)] focus:ring-1 focus:ring-[var(--brand-accent)] bg-slate-50/50"
              />
              <Button
                size="sm"
                onClick={handleApplyCoupon}
                disabled={loading}
                className="h-11 px-5 bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white text-xs font-bold rounded-xl cursor-pointer shadow-xs transition-all shrink-0"
              >
                {loading ? LABELS.CART_SUMMARY.COUPON_APPLYING : LABELS.CART_SUMMARY.COUPON_APPLY}
              </Button>
            </div>
          )}
          {error && <p className="text-xs text-rose-500 font-medium">{error}</p>}
        </div>
      )}

      {/* Cost Breakdown Rows */}
      <div className="space-y-3 text-xs text-slate-600 border-t border-slate-100 pt-4">
        <div className="flex justify-between items-center">
          <span className="font-medium text-slate-600">{LABELS.CART_SUMMARY.SUBTOTAL}</span>
          <Price amount={Number(subtotal)} className="font-semibold text-slate-900 text-sm" />
        </div>

        <div className="flex justify-between items-center">
          <span className="font-medium text-slate-600">Shipping</span>
          <span className="text-[11px] text-slate-400 font-medium">Calculated at Checkout</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="font-medium text-slate-600">{LABELS.CART_SUMMARY.TAX}</span>
          <span className="text-[11px] text-slate-400 font-medium">Calculated at Checkout</span>
        </div>

        {couponApplied && discount > 0 && (
          <div className="flex justify-between items-center text-emerald-600 font-semibold">
            <span>{LABELS.CART_SUMMARY.DISCOUNT}</span>
            <Price amount={discount} className="font-bold text-sm" />
          </div>
        )}

        <div className="border-t border-slate-100 pt-3 flex justify-between items-baseline">
          <span className="text-sm font-bold text-slate-900">Estimated Total</span>
          <Price amount={total} className="text-xl font-black text-slate-900" />
        </div>
      </div>

      {/* Primary & Secondary Action Buttons */}
      <div className="space-y-2.5 pt-2">
        <Button
          onClick={() => fastPush(`/checkout`)}
          disabled={cartItems.length === 0}
          className={`w-full h-12 ${HEADER_V2_DESIGN_SYSTEM.colors.primaryBg} hover:opacity-95 text-white text-xs font-bold uppercase tracking-wider rounded-xl cursor-pointer shadow-md transition-all flex items-center justify-center gap-2`}
        >
          <span>{LABELS.CART_SUMMARY.CHECKOUT_BUTTON}</span>
          <ArrowRight className="w-4 h-4" />
        </Button>

        <button
          type="button"
          onClick={() => fastPush('/')}
          className="w-full h-11 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold uppercase tracking-wider rounded-xl transition-colors cursor-pointer"
        >
          {LABELS.CART.CONTINUE_SHOPPING || "Continue Shopping"}
        </button>
      </div>

      {/* Trust & SSL Security Box */}
      <div className="border-t border-slate-100 pt-4 space-y-3 text-[11px] text-slate-500">
        <div className="flex items-center justify-center gap-1.5 font-bold text-slate-800 text-xs">
          <Lock className="w-3.5 h-3.5 text-emerald-600" />
          <span>Secure SSL Checkout</span>
        </div>

        <div className="space-y-1.5 text-slate-500 font-medium text-center">
          <p className="flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span>256-bit encrypted payments</span>
          </p>
          <p className="flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span>Visa • Mastercard • Stripe • PayPal</span>
          </p>
          <p className="flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span>Easy Returns • Trusted by 10,000+ Customers</span>
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Main Order Summary Card (Target for Intersection Observer) */}
      <div
        ref={summaryRef}
        id="mobile-order-summary"
        className="bg-white rounded-[12px] border border-slate-200/80 p-5 sm:p-6 shadow-sm space-y-5"
      >
        <h2 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3">
          {LABELS.CART_SUMMARY.TITLE}
        </h2>
        {renderSummaryDetails()}
      </div>

      {/* Adaptive Floating Sticky Bottom Bar (Mobile Only, automatically disappears when summary entering viewport) */}
      {cartItems.length > 0 && (
        <div
          className={`md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/80 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] transition-all duration-300 transform ${
            isSummaryInView
              ? "translate-y-full opacity-0 pointer-events-none"
              : "translate-y-0 opacity-100"
          }`}
        >
          <div className="flex items-center justify-between gap-3 max-w-md mx-auto">
            {/* Left Column: Total & View Summary link */}
            <div className="flex flex-col">
              <div className="flex items-baseline gap-1.5">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">ORDER TOTAL</span>
                <Price amount={total} className="text-base font-black text-slate-900" />
              </div>
              <button
                type="button"
                onClick={handleScrollToSummary}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--brand-accent)] hover:underline cursor-pointer transition-colors mt-0.5 text-left"
              >
                <span>▼ View Summary</span>
              </button>
            </div>

            {/* Right Column: Primary Checkout Button */}
            <Button
              onClick={() => fastPush(`/checkout`)}
              className={`h-11 px-5 ${HEADER_V2_DESIGN_SYSTEM.colors.primaryBg} text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-md cursor-pointer transition-all flex items-center gap-1.5 shrink-0`}
            >
              <span>Checkout</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
